import React from "react";
import { Link } from "react-router-dom";
import { MarketingLayout } from "../../components/marketing/MarketingLayout";
import { MarketingCTA } from "../../components/marketing/MarketingCTA";
import { SEO } from "../../components/seo/SEO";
import { breadcrumbSchema } from "../../components/seo/schema";
import { getMarketingIcon } from "../../data/marketingIcons";
import { FEATURES_BY_SLUG } from "../../data/featuresData";

const GROUPS = [
  { label: "Operations", slugs: ["crm", "dispatching", "job-management", "scheduling", "customer-management", "sales-pipeline"] },
  { label: "Money", slugs: ["estimates", "invoicing", "online-payments", "pricebook", "memberships", "payroll", "time-tracking"] },
  { label: "AI", slugs: ["ai-assistant", "ai-diagnostic-assistant", "ai-voice-agents", "ai-receptionist", "ai-knowledge-base"] },
  { label: "Field Operations", slugs: ["mobile-app", "gps-fleet-tracking"] },
  { label: "Communication", slugs: ["team-messaging", "shared-inbox", "marketing-automation", "reputation-management", "automations"] },
  { label: "Insights", slugs: ["reports"] },
];

export default function FeaturesIndex() {
  const breadcrumb = [{ name: "Home", path: "/" }, { name: "Features", path: "/features" }];
  return (
    <MarketingLayout breadcrumb={breadcrumb}>
      <SEO
        title="Home Service CRM Features | ServiceGuru Pro"
        description="Explore every ServiceGuru Pro feature — CRM, dispatching, estimates, invoicing, payroll, and AI voice agents built for home service and contractor businesses."
        path="/features"
        jsonLd={[breadcrumbSchema(breadcrumb)]}
      />
      <section className="max-w-4xl mx-auto px-5 lg:px-8 pt-12 pb-14 text-center">
        <h1 className="text-4xl sm:text-5xl font-black tracking-tight leading-[1.05] mb-5" data-testid="features-index-h1">
          Everything In One Platform
        </h1>
        <p className="text-[15.5px] text-slate-400 leading-relaxed max-w-2xl mx-auto">
          From the first phone call to the final invoice — dispatching, CRM, estimates, payroll, and AI voice agents, all in ServiceGuru Pro.
        </p>
      </section>

      {GROUPS.map((group) => (
        <section key={group.label} className="max-w-6xl mx-auto px-5 lg:px-8 pb-14">
          <h2 className="text-xl font-bold text-white mb-5">{group.label}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {group.slugs.map((slug) => {
              const f = FEATURES_BY_SLUG[slug];
              if (!f) return null;
              const Icon = getMarketingIcon(f.icon);
              return (
                <Link
                  key={slug}
                  to={`/features/${slug}`}
                  data-testid={`features-index-card-${slug}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 hover:border-blue-400/50 hover:bg-white/[0.04] transition-colors group"
                >
                  <div className="h-10 w-10 rounded-xl bg-blue-600/15 border border-blue-500/20 flex items-center justify-center mb-3">
                    <Icon className="h-5 w-5 text-blue-400" />
                  </div>
                  <h3 className="text-white font-semibold text-[14.5px] mb-1 group-hover:text-blue-400 transition-colors">{f.title}</h3>
                  <p className="text-[13px] text-slate-500 leading-relaxed">{f.intro.slice(0, 90)}…</p>
                </Link>
              );
            })}
          </div>
        </section>
      ))}

      <MarketingCTA heading="See every feature in action" sub="Start your 14-day free trial — no credit card required." />
    </MarketingLayout>
  );
}
